import { useEffect, useState, useMemo, useRef } from 'react';
import { TpTemplateInfo } from '../../api/models/assets';
import Expander from '../../components/expander/Expander';
import {
  closeOpenedAsset,
  modifyAsset,
  selectQueriedAssets,
  setFetchedAssets,
  setSortBy,
} from '../../data/assets/queriedAssetsSlice';
import AssetOverview from './elements/AssetOverview';
import QuickAccess from './elements/QuickAccess';
import SearchFilter from './elements/SearchFilter';
import DataUploads from './elements/DataUploads';
import AssetDashboard from './elements/AssetDashboard';
import { useAppDispatch, useAppSelector } from '../../app/hooks';

import { currentTheme } from '../themes/themes';
import { TpAssetQueryParams } from '../../api/models/apis';
import { useApiContext } from '../../api';
import { LocalErrorBoundary } from '../../components/error-boundary/LocalErrorBoundary';

import { getDefaultLimit, getDefaultSortOption } from './StartPageTemplateUtils';

interface TpStartPageViewFactoryProps {
  startPageViews: { [key: string]: TpTemplateInfo[] };
  ontology: any;
}

export function StartPageViewFactory({ startPageViews, ontology }: TpStartPageViewFactoryProps) {
  const queriedAssetsData = useAppSelector(selectQueriedAssets);
  const typeFilter = queriedAssetsData.typeFilter;
  const searchText = queriedAssetsData.searchText;
  const sortBy = queriedAssetsData.sortBy;
  const sortAsc = queriedAssetsData.sortAsc;
  const page = queriedAssetsData.page;

  const dispatch = useAppDispatch();
  const api = useApiContext();

  const startPageTemplate = startPageViews.default;
  const limit = useMemo(() => getDefaultLimit(startPageTemplate), [startPageTemplate]);

  const [isFetching, setIsFetching] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const latestRequestIdRef = useRef(0);

  const componentMap: any = {
    quickAccess: QuickAccess,
    searchFilter: SearchFilter,
    assetOverview: AssetOverview,
    dataUploads: DataUploads,
    assetDashboard: AssetDashboard,
  };

  const fetchAssets = () => {
    if (limit !== null && page !== null) {
      // Prevent out-of-order responses
      const requestId = ++latestRequestIdRef.current;
      setIsFetching(true);

      const params: TpAssetQueryParams = {
        limit: limit,
        offset: page * limit,
        typeFilter: typeFilter,
        searchText: searchText,
        sort: sortBy,
        asc: sortAsc,
        isSystemAsset: false, // Exclude system assets by default on the start page.
      };
      api.asset
        .listAssets(params)
        .then((response) => {
          // Only process the latest response
          if (requestId !== latestRequestIdRef.current) return;
          if (response) {
            dispatch(setFetchedAssets({ assets: response.assets, queryStats: response.queryStats }));
          }
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          // Only update fetching state for the latest request
          if (requestId === latestRequestIdRef.current) setIsFetching(false);
        });
    }
  };

  useEffect(() => {
    if (!startPageTemplate) {
      throw new Error('No start page template found');
    }
    if (!sortBy) {
      const [defaultSortBy, defaultSortAsc] = getDefaultSortOption(startPageTemplate);
      dispatch(setSortBy({ sortBy: defaultSortBy, asc: defaultSortAsc }));
    }
    if (queriedAssetsData.openedAssets.length > 0) {
      const queriedAssetIds = queriedAssetsData.queriedAssets.map((asset) => asset.id);
      for (const asset of queriedAssetsData.openedAssets) {
        if (!queriedAssetIds.includes(asset.id)) {
          dispatch(closeOpenedAsset({ assetId: asset.id }));
        } else {
          api.asset
            .fetchAsset(asset.id)
            .then((response) => {
              if (response) {
                dispatch(modifyAsset({ asset: response }));
              }
            })
            .catch((error) => {
              if (error.response.status === 404) dispatch(closeOpenedAsset({ assetId: asset.id }));
            });
        }
      }
    }
    setIsReady(true);
  }, []);

  useEffect(() => {
    if (isReady) {
      fetchAssets();
    }
  }, [isReady, searchText, typeFilter, limit, page, sortBy, sortAsc]);

  return (
    <div key="start-page">
      {isReady &&
        startPageTemplate &&
        startPageTemplate.startPageElements &&
        startPageTemplate.startPageElements.map((element: any, index: string) => {
          const Component = componentMap[element.type];

          if (Component) {
            let comp = (
              <LocalErrorBoundary key={element.type} resetKeys={[element, ontology, isFetching]}>
                <Component
                  {...element}
                  key={element.type}
                  ontology={ontology}
                  assets={queriedAssetsData.fetchedAssets}
                  queryStats={queriedAssetsData.queryStats}
                  searchText={searchText}
                  typeFilter={typeFilter}
                  sortBy={sortBy}
                  sortAsc={sortAsc}
                  page={page}
                  dashboardtemplate={element}
                  limit={limit}
                  fetchAssets={fetchAssets}
                  isFetching={isFetching}
                />
              </LocalErrorBoundary>
            );
            if (element.collapsedMaxHeight)
              comp = (
                <Expander
                  collapsedMaxHeight={element.collapsedMaxHeight}
                  backgroundColor={currentTheme.colors.background2}
                >
                  {comp}
                </Expander>
              );
            return comp;
          }
          return <div key={index}>Invalid Component Key: {element.key}</div>;
        })}
    </div>
  );
}
